/**
 * Standard currency formatting utility for the entire application
 */

/**
 * Currency symbol mapping for supported currencies
 * Used for direct currency code to symbol lookup
 */
export const CURRENCY_SYMBOLS = {
    USD: '$',
    SGD: 'S$',
    MYR: 'RM',
    IDR: 'Rp',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
    JPY: '¥',
    PHP: '₱',
    THB: '฿',
    VND: '₫',
    AUD: 'A$',
    HKD: 'HK$',
    INR: '₹',
    KRW: '₩'
};

/**
 * Get currency symbol from ISO 4217 currency code
 * @param {string} currencyCode - Currency code like 'USD', 'SGD', 'MYR'
 * @returns {string} Currency symbol (defaults to '$' if not found)
 */
export const getCurrencySymbolByCode = (currencyCode) => {
    if (!currencyCode) return '$';
    return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] || '$';
};

export const formatCurrency = (value, currencySymbol = '$') => {
    if (value === null || value === undefined || isNaN(value)) {
        return currencySymbol + '0';
    }

    return currencySymbol + parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
        maximumFractionDigits: 2
    });
};

/**
 * Format currency without decimals (truncates, does not round)
 * Used for reports where decimal precision is not needed
 * @param {number} value - The numeric value
 * @param {string} currencySymbol - Currency symbol (default: '$')
 * @returns {string} Formatted currency string without decimals
 */
export const formatCurrencyWhole = (value, currencySymbol = '$') => {
    if (value === null || value === undefined || isNaN(value)) {
        return currencySymbol + '0';
    }

    // Truncate decimals (not round)
    const truncated = Math.trunc(parseFloat(value));

    return currencySymbol + truncated.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });
};

/**
 * Format currency value and return metadata about negative status
 * Used by CurrencyValue component to handle negative amounts with parentheses and red color
 * @param {number} value - The numeric value
 * @param {string} currencySymbol - Currency symbol (default: '$')
 * @returns {object} { formatted: string, isNegative: boolean }
 */
export const formatCurrencyValue = (value, currencySymbol = '$') => {
    if (value === null || value === undefined || isNaN(value)) {
        return { formatted: currencySymbol + '0.00', isNegative: false };
    }

    const numValue = parseFloat(value);

    // Prevent -0.00 by checking if value rounds to 0
    const roundedValue = Math.round(numValue * 100) / 100;
    if (roundedValue === 0) {
        return { formatted: currencySymbol + '0.00', isNegative: false };
    }

    const isNegative = roundedValue < 0;
    const absValue = Math.abs(roundedValue);

    const formattedAmount = currencySymbol + absValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    // Return parentheses format for negative values
    return {
        formatted: isNegative ? `(${formattedAmount})` : formattedAmount,
        isNegative
    };
};

export const formatCurrencyWithMode = (value, isUSD, projectCurrency) => {
    const currencySymbol = isUSD ? '$' : (projectCurrency?.symbol || '$');
    return formatCurrency(value, currencySymbol);
};

/**
 * Format currency value with mode and return metadata about negative status
 * @param {number} value - The numeric value
 * @param {boolean} isUSD - Whether to show in USD
 * @param {object} projectCurrency - Project currency object with symbol
 * @returns {object} { formatted: string, isNegative: boolean }
 */
export const formatCurrencyValueWithMode = (value, isUSD, projectCurrency) => {
    const currencySymbol = isUSD ? '$' : (projectCurrency?.symbol || '$');
    return formatCurrencyValue(value, currencySymbol);
};

// Helper to determine if we're in USD mode based on various inputs
export const isUSDMode = (currencyMode) => {
    return currencyMode === 'US Dollar' || currencyMode === 'USD' || currencyMode === true;
};

// Helper to get currency symbol
export const getCurrencySymbol = (isUSD, projectCurrency) => {
    return isUSD ? '$' : (projectCurrency?.symbol || '$');
};

/**
 * Get currency highlighting styles based on mode
 * @param {string} currencyMode - 'US Dollar' or 'Project Currency'
 * @returns {object} Style object for React components
 */
export const getCurrencyHighlightStyles = (currencyMode) => {
    const isUSD = currencyMode === 'US Dollar';
    return {
        padding: '2px 6px',
        borderRadius: '4px',
        backgroundColor: isUSD ? 'rgba(254, 210, 48, 0.15)' : 'transparent',
        border: 'none',
        display: 'inline-block',
        transition: 'all 0.3s ease'
    };
};

/**
 * Format currency with mode (USD or Project Currency)
 * @param {number} value - The numeric value
 * @param {string} currencyMode - 'US Dollar' or 'Project Currency'
 * @param {object} project - Project object containing currency info
 * @returns {string} Formatted currency string
 */
/**
 * Format currency value with 0 decimals and return metadata about negative status
 * Used by CurrencyValue component when showDecimals is false
 * @param {number} value - The numeric value
 * @param {string} currencySymbol - Currency symbol (default: '$')
 * @returns {object} { formatted: string, isNegative: boolean }
 */
export const formatCurrencyValueWhole = (value, currencySymbol = '$') => {
    if (value === null || value === undefined || isNaN(value)) {
        return { formatted: currencySymbol + '0', isNegative: false };
    }

    const numValue = parseFloat(value);
    const rounded = Math.round(numValue);

    if (rounded === 0) {
        return { formatted: currencySymbol + '0', isNegative: false };
    }

    const isNegative = rounded < 0;
    const formattedAmount = currencySymbol + Math.abs(rounded).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    return {
        formatted: isNegative ? `(${formattedAmount})` : formattedAmount,
        isNegative
    };
};

export const formatCurrencyByMode = (value, currencyMode, project) => {
    const isUSD = currencyMode === 'US Dollar';
    const currencySymbol = isUSD ? '$' : (project?.currency?.symbol || '$');
    return formatCurrency(value, currencySymbol);
};

// =======================================
// CSV Export Helpers
// =======================================

/**
 * Escape a cell value for CSV output (RFC 4180 compliant)
 * Handles quotes, commas, and newlines properly
 * @param {*} cell - Cell value to escape
 * @returns {string} Escaped CSV cell
 */
export const escapeCSV = (cell) => {
    if (cell === null || cell === undefined) return '';
    const str = String(cell);
    // If cell contains quotes, commas, or newlines, wrap in quotes and escape internal quotes
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

/**
 * Generate CSV content from headers and rows
 * @param {string[]} headers - Array of header strings
 * @param {Array<Array<*>>} rows - 2D array of row data
 * @returns {string} CSV content string ready for download
 */
export const generateCSV = (headers, rows) => {
    const headerLine = headers.map(escapeCSV).join(',');
    const dataLines = rows.map(row => row.map(escapeCSV).join(','));
    return [headerLine, ...dataLines].join('\n');
};

/**
 * Trigger a CSV file download in the browser
 * @param {string} csvContent - The CSV content string
 * @param {string} filename - The filename for the download
 */
export const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
};

// =======================================
// Percentage Formatting
// =======================================

/**
 * Format a percentage value for display
 * @param {number} value - The percentage value (e.g., 25.5 for 25.5%)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '0.00%';
    }
    return `${parseFloat(value).toFixed(decimals)}%`;
};

// =======================================
// Date Formatting for Financial Reports
// =======================================

/**
 * Format a month-end date for display (e.g., "October 2025")
 * @param {string} monthEnd - Month-end date string (YYYY-MM-DD)
 * @returns {string} Formatted month string
 */
export const formatMonthLabel = (monthEnd) => {
    if (!monthEnd) return '';
    const date = new Date(monthEnd + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/**
 * Format a month-end date as short label (e.g., "Oct 2025")
 * @param {string} monthEnd - Month-end date string (YYYY-MM-DD)
 * @returns {string} Short formatted month string
 */
export const formatMonthShort = (monthEnd) => {
    if (!monthEnd) return '';
    const date = new Date(monthEnd + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// =======================================
// Local Currency Formatting (Code Prefix)
// =======================================

/**
 * Format currency with ISO code prefix instead of symbol (e.g., "MYR 1,234.56")
 * Used when displaying local currency amounts alongside USD amounts
 * @param {number} value - The numeric value
 * @param {string} currencyCode - ISO currency code (e.g., 'MYR', 'SGD')
 * @returns {string} Formatted currency with code prefix
 */
export const formatCurrencyWithCode = (value, currencyCode) => {
    if (value === null || value === undefined || isNaN(value)) {
        return `${currencyCode || 'USD'} 0.00`;
    }
    return `${currencyCode || 'USD'} ` + parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

/**
 * Format currency with always showing 2 decimal places (USD style)
 * Used for financial reports that need consistent decimal display
 * @param {number} value - The numeric value
 * @returns {string} Formatted currency string with 2 decimals (e.g., "$1,234.56")
 */
export const formatCurrencyFixed = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '$0.00';
    }
    return '$' + parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};